import {
	CheckMarkGreenUnfilled,
	CloseThin,
	EllipsisOrange
} from '@/resources/assets/images';
import React, { useEffect, useMemo, useState } from 'react';
import BusinessPreviewEmailModal from '@/modules/dashboard/components/BusinessPreviewEmailModal/BusinessPreviewEmailModal';
import type { Business } from '@/modules/dashboard/dashboardTypes';
import CroppedText from '@/components/CroppedText/CroppedText';
import RichEditor from '@/components/RichEditor/RichEditor';
import Button from '@/components/Button/Button';
import {
	getSequencesAISnippets,
	initiateSequence
} from '@/services/sequencesService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import Loader from '@/components/Loader/Loader';
import { sendAnalytics } from '@/services/analyticsService';
import toast from 'react-hot-toast';
import { useSequences } from '../hooks/useSequences';
import { useBusinessInfiniteScroll } from '../hooks/useBusinessInfiniteScroll';
import InfiniteScroll from '../components/InfiniteScroll';
import { useSequencesContext } from '../SequencesContext';

export default function SequencesSendAll() {
	const {
		selectedSequence,
		setShowSendAll,
		selectedBusinesses,
		selectedLists,
		businessesToRemove,
		businessesToInclude,
		modifiedEmails,
		unapprovedBusinesses,
		setSequencePage,
		setModifiedEmails,
		businessesAlreadyInSequence,
		setActiveTab,
		allBusinessesSelect,
		isEnrollOpen,
		setIsEnrollOpen
	} = useSequencesContext();

	const { fetchSequences, resetSequenceState } = useSequences();
	const { isBusinessesLoading } = useSequencesContext();
	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const { onIntersect } = useBusinessInfiniteScroll();

	const [selectedBusinessToDisplay, setSelectedBusinessToDisplay] =
		useState<Business>(selectedBusinesses[0]);

	const [isInitiateLoading, setIsInitiateLoading] = useState(false);

	useEffect(() => {
		return () => {
			setIsInitiateLoading(false);
			// setSelectedBusinessToDisplay(undefined);
		};
	}, []);

	function closeModal() {
		setShowSendAll(false);
		setModifiedEmails([]);
		resetSequenceState();
	}

	function goBack() {
		setModifiedEmails([]);
		setShowSendAll(false);
		setIsEnrollOpen(true);
	}

	function closeSendAllModal(event: React.MouseEvent<HTMLDivElement>) {
		// Check if the event target is the same as the current target
		if (event.currentTarget === event.target) {
			closeModal();
		}
	}

	function handleCreateSendAllRequest() {
		if (!selectedSequence?.id) return;
		setIsInitiateLoading(true);

		// If the user has clicked on select all businesses (it's by default) then we send all list selected to the backend
		// Otherwise we dont send any list
		const listIds = selectedLists.map(l => l.id);

		// Same here but with the excluded businesses
		const excludedBusinessIds =
			allBusinessesSelect === 'SELECTED_ALL'
				? [...businessesToRemove, ...unapprovedBusinesses]
				: undefined;

		const includedBusinesses =
			allBusinessesSelect === 'UNSELECTED_ALL'
				? businessesToInclude.filter(
						business =>
							!unapprovedBusinesses.includes(business.businessId)
					)
				: undefined;

		initiateSequence({
			campaignId: selectedSequence?.id,
			listIds,
			excludedBusinessIds,
			includedBusinesses,
			modifiedEmails
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error initiating sequence. Please try again later.'
					);
				}
				closeModal();
				setSequencePage('MY-SEQUENCES');
				setActiveTab('SEQUENCE');
				fetchSequences();
				toast.success('Sequence initiated successfully!');
			})
			.catch(() => {
				toast.error('Unexpected error. Please try again later.');
			})
			.finally(() => {
				setIsInitiateLoading(false);
			});

		if (selectedSequence.id) {
			// Sequence already exists
			sendAnalytics({
				type: 'track',
				userId: user.email,
				sequenceId: selectedSequence.id,
				eventName: 'user_enrolled_businesses_sequences'
			});
		} else {
			// Sequence does not exist
			sendAnalytics({
				type: 'track',
				userId: user.email,
				sequenceId: selectedSequence.id,
				eventName: 'user_set_sequence_live'
			});
		}
	}

	const listNames = selectedLists.map(l => l.name).join(', ');

	const availableBusinessesToSend = useMemo(
		() =>
			selectedBusinesses.filter(
				b =>
					!businessesAlreadyInSequence.has(b._id) &&
					(b.email ||
						b.facebook?.email ||
						b.web?.email ||
						(b.userAddedData?.map(
							a => a.type === 'email' && a.checked
						).length || 0) > 0)
			),
		[selectedBusinesses, businessesAlreadyInSequence]
	);

	function changeEmailToSend(email: string) {
		setModifiedEmails(
			modifiedEmails.map(m =>
				m.businessId === selectedBusinessToDisplay._id
					? { ...m, email }
					: m
			)
		);
	}

	const emailToSend = useMemo(
		() =>
			modifiedEmails.find(
				m => m.businessId === selectedBusinessToDisplay._id
			)?.email ||
			selectedBusinessToDisplay.email ||
			selectedBusinessToDisplay.facebook?.email ||
			selectedBusinessToDisplay.web?.email ||
			selectedBusinessToDisplay.userAddedData?.find(
				a => a.type === 'email' && a.checked
			)?.data ||
			'',
		[selectedBusinessToDisplay, modifiedEmails]
	);

	const noBusinessSelected =
		availableBusinessesToSend.length === 0 ||
		availableBusinessesToSend.length === unapprovedBusinesses.length;

	return (
		<div
			onClick={goBack}
			className="fixed left-0 top-0 z-150 flex h-screen w-screen items-center justify-center bg-black bg-opacity-50"
		>
			<div
				style={{
					maxWidth: '1300px'
				}}
				className="relative flex h-4/5 w-4/5 flex-col rounded-lg border border-dark-grey bg-dm-black px-10 pb-8 pt-12"
			>
				<CloseButton closeSendAllModal={goBack} />
				<div>
					<span className="text-2xl text-orange">
						<CroppedText text={listNames} maxLength={70} />
					</span>
					<span className="text-gray-100">
						(Only businesses with emails appear)
					</span>
				</div>
				<div className="m-auto flex h-full w-full overflow-hidden rounded-2xl border border-dark-grey">
					<div
						style={{
							width: '700px'
						}}
						className="flex w-full flex-col overflow-y-auto overflow-x-hidden border-r border-dark-grey"
					>
						{availableBusinessesToSend.map(b => (
							<BusinessInSendAllItem
								isSelected={
									selectedBusinessToDisplay._id === b._id
								}
								business={b}
								setSelectedBusinessToDisplay={
									setSelectedBusinessToDisplay
								}
								key={b._id}
							/>
						))}
						<InfiniteScroll onIntersect={onIntersect} />
						{isBusinessesLoading && (
							<tr className="flex h-8 items-center justify-center">
								<td>
									<Loader className="w-5" />
								</td>
							</tr>
						)}
					</div>
					<div
						style={{
							width: '700px'
						}}
						className="overflow-x-hidden border-r border-dark-grey"
					>
						{/* @ts-ignore */}
						<BusinessPreviewEmailModal
							user={user}
							business={selectedBusinessToDisplay}
							to={emailToSend}
							setTo={changeEmailToSend}
							noEdit
						/>
					</div>
					<SendAllEmailEdit
						selectedBusinessToDisplay={selectedBusinessToDisplay}
						emailToSend={emailToSend}
					/>
				</div>
				<div className="mt-5 flex justify-end gap-16">
					<button
						onClick={goBack}
						className="text-lg font-medium text-orange"
					>
						Go back
					</button>
					<Button
						onClick={handleCreateSendAllRequest}
						className="w-min"
						disabled={isInitiateLoading || noBusinessSelected}
					>
						{isInitiateLoading
							? 'Initiating...'
							: 'Set sequence live'}
					</Button>
				</div>
			</div>
		</div>
	);
}

function SendAllEmailEdit({
	selectedBusinessToDisplay,
	emailToSend
}: {
	selectedBusinessToDisplay: Business;
	emailToSend: string;
}) {
	const {
		selectedSequence,
		businessesToRemove,
		setUnapprovedBusinesses,
		modifiedEmails,
		setModifiedEmails
	} = useSequencesContext();
	const [emailBody, setEmailBody] = useState('');
	const [loadingAISnippet, setLoadingAISnippet] = useState(false);

	const replaceVariables = (value: string): string => {
		let updatedValue = value?.replaceAll(
			/\[business_name\]/gi,
			selectedBusinessToDisplay?.name ?? ''
		);
		updatedValue = updatedValue?.replaceAll(
			/\[list_name\]/gi,
			selectedBusinessToDisplay?.listName ?? ''
		);
		return updatedValue;
	};

	const isSelected = !businessesToRemove.includes(
		selectedBusinessToDisplay._id
	);

	const firstStepOfSequence = selectedSequence?.steps[0];

	const bodyReplaced = replaceVariables(firstStepOfSequence?.body || '');
	const subjectReplaced = replaceVariables(
		firstStepOfSequence?.subject || ''
	);

	const hasAISnippet = bodyReplaced.includes('[ai_snippet]');

	const hasToBeGenerated =
		hasAISnippet &&
		!modifiedEmails
			.map(b => b.businessId)
			.includes(selectedBusinessToDisplay._id);

	useEffect(() => {
		if (hasAISnippet && hasToBeGenerated) {
			setLoadingAISnippet(true);
			getSequencesAISnippets({
				businessId: selectedBusinessToDisplay._id,
				emailBody: bodyReplaced
			})
				.then(res => {
					if (res.data) {
						const aiSnippedModified = replaceVariables(
							bodyReplaced.replace(
								'[ai_snippet]',
								res.data.data.snippet
							)
						);
						setEmailBody(aiSnippedModified);
						setModifiedEmails(prev => [
							...prev,
							{
								businessId: selectedBusinessToDisplay._id,
								subject: subjectReplaced,
								body: aiSnippedModified,
								emailToSend
							}
						]);
					}
				})
				.finally(() => setLoadingAISnippet(false));
			return;
		}
		// If the business has an email already created, we use it
		const existingEmailCreated = modifiedEmails.find(
			email => email.businessId === selectedBusinessToDisplay._id
		);
		if (existingEmailCreated) {
			setEmailBody(existingEmailCreated.body);
			return;
		}
		if (!existingEmailCreated) {
			// @ts-ignore
			setModifiedEmails(prev => [
				...prev,
				{
					businessId: selectedBusinessToDisplay._id,
					subject: subjectReplaced,
					body: bodyReplaced,
					email: emailToSend
				}
			]);
			setEmailBody(bodyReplaced);
		}
	}, [selectedBusinessToDisplay]);

	function handleUnapproveEmail() {
		if (isSelected) {
			// @ts-ignore
			setUnapprovedBusinesses(prev => [
				...prev,
				selectedBusinessToDisplay._id
			]);
			return;
		}

		// @ts-ignore
		setUnapprovedBusinesses(prev =>
			// @ts-ignore
			prev.filter(id => id !== selectedBusinessToDisplay._id)
		);
	}

	return (
		<div className="flex h-full w-full flex-col gap-4 overflow-x-hidden p-7">
			<input
				type="text"
				className="w-full bg-gray-200 text-dm-black"
				value={subjectReplaced}
				disabled
			/>
			{loadingAISnippet ? (
				<div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gray-200">
					<Loader />
					<span>Generating AI Snippet</span>
				</div>
			) : (
				<RichEditor
					onChange={() => {}}
					value={emailBody}
					className="h-full w-full bg-gray-200 text-dm-black"
					disabled
					disableDisabledStyles
				/>
			)}

			<button
				onClick={handleUnapproveEmail}
				className="w-min rounded-xl border border-orange-dark px-8 py-2 text-sm text-orange"
			>
				{isSelected ? 'Unapprove' : 'Approve'}
			</button>
		</div>
	);
}

function BusinessInSendAllItem({
	isSelected,
	business,
	setSelectedBusinessToDisplay
}: {
	isSelected: boolean;
	business: Business;
	setSelectedBusinessToDisplay: (business: Business) => void;
}) {
	function selectBusinessToDisplay() {
		setSelectedBusinessToDisplay(business);
	}
	return (
		<div
			onClick={selectBusinessToDisplay}
			className={`flex h-16 w-full flex-shrink-0 cursor-default items-center justify-around border-b-2 border-blue-600 px-4 text-left ${
				isSelected
					? 'bg-orange text-black'
					: 'bg-dm-black3.5 text-white hover:bg-gray-600'
			} `}
		>
			<CroppedText text={business.name} maxLength={25} />
			<Toggle businessId={business._id} />
		</div>
	);
}

function Toggle({ businessId }: { businessId: string }) {
	const { unapprovedBusinesses, setUnapprovedBusinesses } =
		useSequencesContext();
	const isSelected = !unapprovedBusinesses.includes(businessId);

	function handleClick(e: any) {
		e.stopPropagation();

		if (isSelected) {
			// @ts-ignore
			setUnapprovedBusinesses(prev => [...prev, businessId]);
			return;
		}
		// @ts-ignore
		setUnapprovedBusinesses(prev => prev.filter(id => id !== businessId));
	}

	const transformValue = isSelected ? 'translateX(55%)' : 'translateX(0)';

	return (
		<button
			onClick={handleClick}
			className="relative h-7 w-16 rounded-xl border border-dark-grey bg-dm-black"
		>
			<div
				style={{
					height: '110%',
					transform: `${transformValue} translateY(2%)`,
					transition: 'transform 0.2s ease-in-out'
				}}
				className="absolute bottom-0 top-0 my-auto flex w-4/6 items-center justify-center rounded-xl border bg-white"
			>
				{isSelected ? <CheckMarkGreenUnfilled /> : <EllipsisOrange />}
			</div>
		</button>
	);
}

function CloseButton({ closeSendAllModal }: { closeSendAllModal: () => void }) {
	return (
		<button className="absolute right-8 top-4">
			<CloseThin
				className="h-4 w-4 text-gray-100"
				onClick={closeSendAllModal}
			/>
		</button>
	);
}
